/**
 * upload-skip-edge (QA follow-up round 3, T9, 2026-10-03): one notion of "the same edge" for the upload retry.
 *
 * When an upload attempt fails, `FileUploadManager.uploadWithRetry` re-resolves an edge and asks the resolver to skip
 * the one that just failed. The two lists it compared never shared a form, so nothing was ever skipped:
 *   - the failed edge is the resolve reply's `uploadDomain`: `https://` + the edge's NCF host label + the API's parent
 *     domain (professorx `EndpointResolver.resolve` -> `URLUtils.tryConvertHostName`), e.g. `https://ncf-qa-2.xconvert.com`;
 *   - the preferred list is `ConnectivityService`'s measured edges, sent as their bare `d`, e.g. `qa-2.xconvert.com`
 *     (the measured row also keeps the NCF echo URL it probed, `ncfEndpoint`).
 * Graylog (qa-fu-round3/graylog/delta-final.txt) showed "Excluding: https://ncf-qa-#..." next to a request whose
 * `preferedDomains` still named that same edge.
 *
 * Here a domain in any of those forms -- bare host, `https://` origin, a URL with a path, the dev proxy's
 * `/__xcdev/h/<host>/...`, a port, upper case -- is reduced to its host, and an edge matches a failed domain when the
 * host equals the edge's `d` or NCF host, or (for a host with 3+ labels) when the first label does: that label is what
 * the resolver swaps onto the API's parent domain, so it is what names the edge.
 */

/** The bare lower-case host of `v` (no scheme, port, path, dev-proxy prefix or trailing dot), or null. */
export function edgeHost(v: unknown): string | null {
    if (typeof v !== 'string') return null;
    let s = v.trim().toLowerCase();
    if (!s) return null;
    s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    // The dev proxy's same-origin path for a host (services/dev-proxy.ts `devProxyOrigin`): `/__xcdev/h/<host>/...`.
    s = s.replace(/^\/?__xcdev\/h\//, '');
    s = s.replace(/^\/+/, '');
    s = s.split(/[/?#]/, 1)[0];
    s = s.replace(/^[^@]*@/, '').replace(/:\d*$/, '').replace(/\.+$/, '');
    return /^[a-z0-9.-]+$/.test(s) ? s : null;
}

/** The edge-naming first label of a host with at least three labels (`ncf-qa-2` of `ncf-qa-2.xconvert.com`), else null. */
function edgeLabel(host: string): string | null {
    const parts = host.split('.');
    return parts.length >= 3 && parts[0] ? parts[0] : null;
}

/** True when two domains, in any of the forms above, name the same edge. */
export function sameEdgeHost(a: unknown, b: unknown): boolean {
    const ha = edgeHost(a);
    const hb = edgeHost(b);
    if (!ha || !hb) return false;
    if (ha === hb) return true;
    const la = edgeLabel(ha);
    return la !== null && la === edgeLabel(hb);
}

/** A measured edge as `ConnectivityService` stores it (`ENDPOINT_RTT`); `ncf` is read too when a row carries it. */
export interface MeasuredEdgeLike {
    domain?: string | null;
    ncf?: string | null;
    ncfEndpoint?: string | null;
}

/** True when `edge` is one of `failedDomains` under any of its names (`d`, NCF host, NCF echo URL). */
export function isFailedEdge(edge: MeasuredEdgeLike | null | undefined, failedDomains: readonly unknown[] | null | undefined): boolean {
    if (!edge || !failedDomains?.length) return false;
    const names = [edge.domain, edge.ncf, edge.ncfEndpoint].filter((n) => !!n);
    return failedDomains.some((failed) => names.some((name) => sameEdgeHost(name, failed)));
}

/**
 * The measured edges minus the ones that failed, in their stored (fastest-first) order. When every measured edge has
 * failed -- one edge, or two that both failed -- the list is returned whole: retrying a reachable edge beats letting
 * the resolver pick at random among edges this browser never reached.
 */
export function withoutFailedEdges<T extends MeasuredEdgeLike>(edges: readonly T[] | null | undefined, failedDomains: readonly unknown[] | null | undefined): T[] {
    const all = (edges || []).filter((e): e is T => !!e);
    if (!failedDomains?.length) return all;
    const kept = all.filter((e) => !isFailedEdge(e, failedDomains));
    return kept.length ? kept : all;
}
